import * as React from "react";
import { t } from "../../i18n";
import { LoginType, SsoProviderStatus, SsoStatus } from "../../http/protocol";

interface SsoConfigSectionProps {
  status?: SsoStatus;
  failed: boolean;
  loaded: boolean;
}

interface SsoProviderRow {
  provider: LoginType;
  displayName: string;
}

const SSO_PROVIDER_ROWS: SsoProviderRow[] = [
  { provider: LoginType.GOOGLE, displayName: "Google" },
  { provider: LoginType.DISCORD, displayName: "Discord" },
  { provider: LoginType.TELEGRAM, displayName: "Telegram" },
];

/**
 * Whether the client-id presence can be trusted. Only the player-facing service knows it, so until that
 * service has reported, `reportedAt` is absent and presence is unknown. The enabled flags come from the
 * admin service itself and remain trustworthy unless the request failed.
 */
export function isSsoStatusUnavailable(status: SsoStatus | undefined, failed: boolean): boolean {
  return failed || !status?.reportedAt;
}

export function findSsoProvider(status: SsoStatus | undefined, provider: LoginType): SsoProviderStatus | undefined {
  return status?.providers?.find((providerStatus) => providerStatus.provider === provider);
}

function ProviderFlagValue({ value, loaded }: { value?: boolean; loaded: boolean }): JSX.Element | null {
  if (!loaded) {
    return null;
  }
  if (value === undefined) {
    return <span className={"text-muted"}>{t("integrations.sso.not-reported", undefined, "not reported")}</span>;
  }
  if (value) {
    return <span className={"badge p-1 badge-success"}>{t("integrations.sso.yes", undefined, "Yes")}</span>;
  }
  return <span className={"badge p-1 badge-secondary"}>{t("integrations.sso.no", undefined, "No")}</span>;
}

/**
 * Read-only view of the SSO providers as the player-facing service reports them. The values come
 * from server configuration shared by every site, so nothing here is editable or bound to the
 * site's Settings form.
 */
export function SsoConfigSection({ status, failed, loaded }: SsoConfigSectionProps): JSX.Element {
  const unavailable = loaded && isSsoStatusUnavailable(status, failed);

  return (
    <div
      className={"card"}
      data-testid={"sso-config-section"}
    >
      <div className={"card-header"}>
        <h4>{t("integrations.sso.title", undefined, "Single Sign-On")}</h4>
        <span className={"help-block"}>
          {t("integrations.sso.description", undefined, "Which social login providers players can use, and whether their credentials are configured.")}{" "}
          {t("integrations.sso.deployment-note", undefined, "Applies to the whole deployment, not just this site.")}
        </span>
      </div>
      <div className="card-body">
        {loaded && failed && (
          <p
            className={"text-muted"}
            data-testid={"sso-status-unavailable"}
          >
            {t("integrations.sso.unavailable", undefined, "SSO status is currently unavailable.")}
          </p>
        )}
        {unavailable && !failed && (
          <p
            className={"text-muted"}
            data-testid={"sso-status-not-yet-reported"}
          >
            {t("integrations.sso.not-yet-reported", undefined, "Credential status has not been reported yet.")}
          </p>
        )}
        <table className={"table"}>
          <thead>
            <tr>
              <th>{t("integrations.sso.provider", undefined, "Provider")}</th>
              <th>{t("integrations.sso.enabled", undefined, "Enabled")}</th>
              <th>{t("integrations.sso.credentials-configured", undefined, "Client id / bot configured")}</th>
            </tr>
          </thead>
          <tbody>
            {SSO_PROVIDER_ROWS.map((row) => {
              const providerStatus = failed ? undefined : findSsoProvider(status, row.provider);
              return (
                <tr
                  key={row.provider}
                  data-testid={`sso-provider-${row.provider.toLowerCase()}`}
                >
                  <td>{row.displayName}</td>
                  <td>
                    <ProviderFlagValue
                      value={providerStatus?.enabled}
                      loaded={loaded}
                    />
                  </td>
                  <td>
                    <ProviderFlagValue
                      value={unavailable ? undefined : providerStatus?.clientIdConfigured}
                      loaded={loaded}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
